import { cloneDeep, debounce } from 'lodash'
import OLGeomLineString from 'ol/geom/LineString'
import OLGeomPoint from 'ol/geom/Point'
import OLInteractionDraw from 'ol/interaction/Draw'
import OLInteractionModify from 'ol/interaction/Modify'
import OLInteractionSnap from 'ol/interaction/Snap'
import OLLayerVector from 'ol/layer/Vector'
import OLRenderFeature from 'ol/render/Feature'
import OLSourceVector from 'ol/source/Vector'
import { getArea, getLength } from 'ol/sphere'
import OLStyleText from 'ol/style/Text'

import type OLFeature from 'ol/Feature'
import type OLGeomGeometry from 'ol/geom/Geometry'
import type { DrawEvent as OLInteractionDrawEvent } from 'ol/interaction/Draw'
import type OLMap from 'ol/Map'
import type OLStyleStyle from 'ol/style/Style'

import PoziMap from 'map/map'
import { getFeatureInfoAtPixel, getMap } from 'utils/MapUtils'
import { formatArea, formatLength } from 'utils/measurement'

export interface StyleCollection {
  defaultPointStyle: OLStyleStyle
  defaultLineStyle: OLStyleStyle | OLStyleStyle[]
  labelStyle?: OLStyleStyle
  modifyStyle?: OLStyleStyle
  segmentStyle?: OLStyleStyle
  snapStyle?: OLStyleStyle
  tooltipStyle?: OLStyleStyle
}

export interface MeasurementToolTips {
  activeTip: string,
  idleTip: string
}

interface MeasurementToolOptions {
  clearOnNewMeasurement?: boolean
  measurementType: string
  onMeasurementComplete?: (event: OLInteractionDrawEvent) => void
  showSegmentLengths: boolean
  showTotalMeasurement: boolean
  snapToFeatures: boolean
  styles: StyleCollection
  toolTips: MeasurementToolTips
}

export const MEASUREMENT_LAYER_TITLEID = 'pozimeasurement'

class MeasurementTool {
  private map: OLMap
  private options: MeasurementToolOptions
  private draw: OLInteractionDraw | undefined
  private modify: OLInteractionModify
  private snap: OLInteractionSnap
  private measureSource: OLSourceVector
  private snapSource: OLSourceVector
  private isSnappedToFeature: boolean = false
  private toolTip: string

  constructor (options: MeasurementToolOptions) {
    this.map = getMap()
    this.options = cloneDeep(options)
    this.toolTip = options.toolTips.idleTip

    this.measureSource = new OLSourceVector()
    this.snapSource = new OLSourceVector()

    const measureLayer = new OLLayerVector({
      source: this.measureSource
    })

    // This layer needs the titleid, so that we can identify it when finding features to snap to in getFeatureInfoAtPixel
    measureLayer.set('titleid', MEASUREMENT_LAYER_TITLEID)

    this.map.addLayer(measureLayer)

    this.map.addLayer(new OLLayerVector({
      source: this.snapSource,
      visible: false
    }))

    this.modify = new OLInteractionModify({
      source: this.measureSource,
      style: this.options.styles?.modifyStyle
    })

    this.snap = new OLInteractionSnap({
      source: this.snapSource
    })

    this.map.on('pointermove', () => {
      this.isSnappedToFeature = false
    })

    this.snap.on('snap', () => {
      this.isSnappedToFeature = true
    })

    this.enable()
  }

  clear () {
    if (this.measureSource) this.measureSource.clear()
  }

  disable () {
    PoziMap().enableDefaultMapClickInteration()
    document.removeEventListener('keydown', this.handleKeyDown)
    this.map.un('pointermove', this.debouncedMouseMoveHandler)
    // OpenLayers removeInteraction can handle when this.draw is null or undefined, so bypass lint rule
    this.map.removeInteraction(this.draw!)
    this.map.removeInteraction(this.modify)
    this.map.removeInteraction(this.snap)
  }

  enable () {
    PoziMap().disableDefaultMapClickInteration()
    // Listen for the Escape key to cancel the current measurement
    document.addEventListener('keydown', this.handleKeyDown)
    this.addDrawingInteraction()
    this.toggleSnapping()
  }

  updateMeasurementOptions (updatedOptions: Partial<MeasurementToolOptions>) {
    const prevOptions = cloneDeep(this.options)

    this.options = {
      ...this.options,
      ...cloneDeep(updatedOptions)
    }

    if (!updatedOptions.measurementType) {
      this.disable()
      return
    }

    if (updatedOptions.measurementType !== prevOptions.measurementType) this.enable()
    if (updatedOptions.snapToFeatures !== prevOptions.snapToFeatures) this.toggleSnapping()
  }

  private addDrawingInteraction () {
    this.map.removeInteraction(this.draw!)
    this.map.removeInteraction(this.modify)
    this.map.removeInteraction(this.snap)

    const geometryType = this.options.measurementType === 'distance' ? 'LineString' : 'Polygon'

    this.draw = new OLInteractionDraw({
      source: this.measureSource,
      type: geometryType,
      style: this.styleMeasurementFeatures
    })

    this.draw.on('drawstart', () => {
      if (this.options.clearOnNewMeasurement) {
        this.measureSource.clear()
      }
      this.toolTip = this.options.toolTips.activeTip
      this.modify?.setActive(false)
    })

    this.draw.on('drawend', (event) => {
      // Set the style of the new feature to retain it
      const { feature } = event
      feature.setStyle(this.styleMeasurementFeatures(feature))

      this.toolTip = this.options.toolTips.idleTip
      this.modify?.setActive(true)

      // Re-apply the custom style during modification
      const featureGeometry = feature.getGeometry()
      if (featureGeometry) {
        featureGeometry.on('change', () => {
          feature.setStyle(this.styleMeasurementFeatures(feature))
        })
      }
      this.options.onMeasurementComplete?.(event)
    })

    this.map.addInteraction(this.draw)
    this.map.addInteraction(this.modify)
    this.map.addInteraction(this.snap)
  }

  private styleMeasurementFeatures = (feature: any): OLStyleStyle[] => {
    const defaultLineStyle = this.options.styles.defaultLineStyle
    const combinedStyles = Array.isArray(defaultLineStyle)
      ? [...defaultLineStyle] // Spread if it's an array
      : [defaultLineStyle] // Wrap in array if it's not

    // Check if the pointer has snapped to a measurment feature ready for modification
    const modifyIsSnapped = (this.modify?.getOverlay()?.getSource()?.getFeatures()?.length ?? 0) > 0

    if (this.isSnappedToFeature && this.options.styles?.snapStyle && !modifyIsSnapped) {
      combinedStyles.push(this.options.styles?.snapStyle)
    } else if (!modifyIsSnapped) {
      combinedStyles.push(this.options.styles.defaultPointStyle)
    }

    const geometry = feature.getGeometry()
    const featureType = geometry.getType()

    const tooltipStyle = this.options.styles?.tooltipStyle
    const tooltipStyleText = tooltipStyle?.getText() || new OLStyleText()

    // If feature is a point, no measurement is currently active
    if (tooltipStyle && featureType === 'Point' && !modifyIsSnapped) {
      tooltipStyleText.setText(this.toolTip)
      tooltipStyle.setText(tooltipStyleText)
      combinedStyles.push(tooltipStyle)
    }

    if (featureType === 'Point') return combinedStyles

    const segmentStyle = this.options.styles?.segmentStyle
    const showSegmentMeasurement = this.options.showSegmentLengths && segmentStyle

    if (showSegmentMeasurement) {
      const segmentStyles = [segmentStyle.clone()]
      const segmentGeometry = featureType === 'Polygon' ? new OLGeomLineString(geometry.getCoordinates()[0]) : geometry

      let count = 0
      segmentGeometry.forEachSegment((startCoord: number, endCoord: number) => {
        const segment = new OLGeomLineString([startCoord, endCoord])
        const label = formatLength(getLength(segment))
        if (segmentStyles.length <= count) {
          segmentStyles.push(segmentStyle.clone())
        }
        segmentStyles[count].setGeometry(segment)
        segmentStyles[count]?.getText()?.setText(label)
        combinedStyles.push(segmentStyles[count])
        count++
      })
    }

    const totalMeasurementStyle = this.options.styles?.labelStyle?.clone()
    const totalMeasurementText = totalMeasurementStyle?.getText() || new OLStyleText()

    // Besides checking if total measurements are selected, if segment length is shown it
    // is not necessary to show total measurement while there is only a single segment drawn
    const hasMinimumVertices = featureType === 'Polygon'
      ? geometry.getCoordinates()[0].length > 3
      : geometry.getCoordinates().length > 2
    const showTotalMeasurement = this.options.showTotalMeasurement &&
      totalMeasurementStyle &&
      (hasMinimumVertices || !this.options.showSegmentLengths)

    if (showTotalMeasurement) {
      if (featureType === 'Polygon') {
        totalMeasurementText.setText(formatArea(getArea(geometry)))
        totalMeasurementStyle.setGeometry(geometry.getInteriorPoint())
        totalMeasurementStyle.setText(totalMeasurementText)
        combinedStyles.push(totalMeasurementStyle)
      } else if (this.options.measurementType === 'distance') {
        totalMeasurementText.setText(formatLength(getLength(geometry)))
        totalMeasurementStyle.setGeometry(new OLGeomPoint(geometry.getLastCoordinate()))
        totalMeasurementStyle.setText(totalMeasurementText)
        combinedStyles.push(totalMeasurementStyle)
      }
    }

    return combinedStyles
  }

  // Debounced handler for mouse move events
  private toggleSnapping = (snapToFeatures: boolean = this.options.snapToFeatures) => {
    this.snap?.setActive(snapToFeatures)
    if (snapToFeatures) {
      this.map.on('pointermove', this.debouncedMouseMoveHandler)
    } else {
      this.map.un('pointermove', this.debouncedMouseMoveHandler)
    }
  }

  // Adds features to the snapping layer as mouse is moved over map
  private debouncedMouseMoveHandler = debounce((event: any) => {
    this.snapSource.clear()

    getFeatureInfoAtPixel(event.pixel, 10)?.forEach((featureInfo) => {
      // Vector tile layers return RenderFeatures instead of regular Features.
      // RenderFeatures have cutdown capabilities, and don't have a getCoordinates method, which
      // means they don't work with snapping.  The flatcoordinates_ property could be used to build
      // a geometry and add it to a feature, but it's a private property of the RenderFeature class
      // so probably not recommended.
      if (featureInfo.olFeature instanceof OLRenderFeature) {
        // console.debug('Ignoring _RenderFeature:', featureInfo.olFeature)
        return
      }
      this.snapSource.addFeature(featureInfo.olFeature)
    })
  }, 50)

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.draw?.abortDrawing()
      this.toolTip = this.options.toolTips.idleTip
      this.modify?.setActive(true)
    }
  }
}

export default MeasurementTool
